import React, { useContext, useState } from "react"
import { useForm } from "react-hook-form"
import styled from "styled-components"
import Breakpoints from "../../components/shared/breakpoints"
import useSiteMetadata from "../../components/shared/hooks/useSiteMetadata"
import { UIContext } from "../../components/shared/layout"
import { T } from "../styles/typography"
import { ButtonStyles } from "./button"

interface IEmailSignupProps {
  endpoint: string
  placeholder?: string
  cta?: string
  primaryColour?: string
  secondaryColour?: string
}

/**
 * Reusable Email Signup Form
 *
 * Pulled from `../../components/shared/footer.email-submission.js`
 * @param endpoint - The endpoint to submit the form to
 * @param placeholder - The placeholder text for the email input
 * @param cta - The text for the submit button
 * @param primaryColour - The primary colour for the submit button
 * @param secondaryColour - The secondary colour for the submit button
 */
const EmailSignup = ({
  endpoint: HUBSPOT_ENDPOINT,
  placeholder = "Your e-mail address",
  cta = "Get Updates",
  primaryColour = "var(--burnt-orange)",
  secondaryColour = "#fff",
}: IEmailSignupProps) => {
  const { register, errors, handleSubmit } = useForm()
  const metadata = useSiteMetadata()
  const [hasSubmitted, setHasSubmitted] = useState(false)
  const [submissionFailure, setSubmissionFailure] = useState(false)
  const {
    state: { location },
  } = useContext(UIContext)
  const submitCallback = (data, e) => {
    e.preventDefault()

    const submissionData = Object.keys(data).reduce((acc, key) => {
      acc.push({
        name: key,
        value: data[key],
      })
      return acc
    }, [])

    const pageUri = metadata.siteUrl + location.pathname
    const submission = {
      fields: [...submissionData],
      context: {
        pageUri,
        pageName: document.title,
      },
    }

    try {
      let hutk = document.cookie
        .split("; ")
        .find((row) => row.startsWith("hubspotutk"))
        .split("=")[1]

      submission.context = {
        ...submission.context,
        // @ts-ignore
        hutk,
      }
    } catch (error) {}

    fetch(HUBSPOT_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(submission),
    })
      .then((response) => response.json())
      .then((data) => {
        if (!data.errors) {
          setSubmissionFailure(false)
          setHasSubmitted(true)
        } else {
          setSubmissionFailure(true)
        }
      })
      .catch((error) => {
        setSubmissionFailure(true)
      })
  }

  const error = submissionFailure
    ? "An error occured while submitting the form"
    : errors.email?.type === "pattern"
    ? "Enter a valid email address"
    : errors.email?.type === "required"
    ? errors.email.message
    : ""

  return (
    <EmailContainer onSubmit={handleSubmit(submitCallback)}>
      {error && <EmailErrorText>{error}</EmailErrorText>}
      <EmailInput
        name="email"
        aria-label="newsletter-signup"
        disabled={hasSubmitted}
        placeholder={placeholder}
        // @ts-ignore
        ref={register({
          required: "This field is required (*)",
          //email regex from https://emailregex.com/
          //eslint-disable-next-line
          pattern:
            /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
          message: "Enter a valid email address",
        })}
      />
      <EmailSubmitButton
        primary={primaryColour}
        secondary={secondaryColour}
        disabled={hasSubmitted}
      >
        {hasSubmitted ? "Subscribed" : cta}
      </EmailSubmitButton>
    </EmailContainer>
  )
}

const EmailContainer = styled.form`
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  flex-basis: 100%;
  @media (min-width: ${Breakpoints.md}) {
    flex-direction: row;
    max-height: 46px;
  }
`
const EmailInput = styled.input`
  box-sizing: border-box;
  ${T.sm.body`400`};
  border: 1px solid #d9d9d9;
  width: 100%;
  padding: 12px;

  background-color: unset;
  margin-bottom: 16px;

  :focus {
    outline: none;
  }

  @media (min-width: ${Breakpoints.md}) {
    max-width: 320px;
    margin: 0 16px 0 0;
  }
`

const EmailErrorText = styled.p`
  ${T.sm.body`400`};
  position: absolute;
  top: -24px;
  color: var(--burnt-orange);
  @media (min-width: ${Breakpoints.md}) {
    bottom: -32px;
    top: unset;
  }
`

const EmailSubmitButton = styled.button<{
  primary: string
  secondary: string
  disabled: boolean
}>`
  ${T.misc.link`500`};
  ${ButtonStyles.DefaultStyle}
  font-size: 12px;
  width: 100%;
  min-width: 100px;
  cursor: pointer;

  text-transform: uppercase;
  justify-content: center;
  @media (min-width: ${Breakpoints.md}) {
    max-width: 175px;
    max-height: 46px;
  }
`

export default EmailSignup
